import { RootState } from "@/store";
import { connect } from "react-redux";
import BestekFilter from "@/components/bestek-filter/bestek-filter";
import { BestekFilterState } from "@/store/reducers/bestek_filter";
import apiService from "@/api-service";
import { Bestek } from "@/models/bestek";
import { useEffect, useState, useCallback } from "react";
import { Badge, Button, ButtonGroup, Col, Container, Dropdown, Form, Modal, Row } from "react-bootstrap";
import BestekkenMap from "@/components/bestekken-map/bestekken-map";
import BestekUploader from "@/components/bestek-uploader/bestek-uploader";
import BestekGeneralInfo from "../detail/general_info/general_info";
import BestekkenList from "@/components/bestek-list/bestek-list";
import { MdClose, MdDownload } from "react-icons/md";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import styles from './list.module.scss';
import CSVExportModal from "@/components/csv-export-modal/csv-export-modal";
import { ZOEK_CATEGORIEEN, ZoekCategorie } from "@/components/bestek-filter/bestek-filter-params";

interface Props {
    bestek_filter: BestekFilterState
}

function mapStateToProps(state: RootState): Props {
    return {
        bestek_filter: state.bestek_filter
    };
}

const BestekkenListPage = (props: Props) => {
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();

    const [ viewType, setViewtype ] = useState<"list" | "map">("list"); 
    const [ selectedBestek, setSelectedBestek ] = useState<Bestek>();

    const [ uploadModalShown, setUploadModalShown ] = useState<boolean>(false);
    // VERWIJDERD: filterShown state - dit wordt nu beheerd door Redux
    const [ bepalingExportModalShown, setBepalingExportModalShown ] = useState<boolean>(false);
    const [ csvExportModalShown, setCsvExportModalShown ] = useState<boolean>(false);

    const [ bepalingExportSearchTexts, setBepalingExportSearchTexts ] = useState<string[]>([]);
    const [ newBepalingExportSearchText, setNewBepalingExportSearchText ] = useState<string>();
    const [ bepalingExportHoofdstuk, setBepalingExportHoofdstuk ] = useState<string>();
    const [ bepalingExportFormat, setBepalingExportFormat ] = useState<'csv' | 'xlsx'>('csv');
    const [ isExportingBepalingen, setIsExportingBepalingen ] = useState<boolean>(false);

    const pageSize: number = 10;
    const [ activePage, setActivePage ] = useState<number>(1);
    const [ totalCount, setTotalCount ] = useState<number>(0);
    const [ bestekken, setBestekken ] = useState<Bestek[]>([]);
    const [ loading, setLoading ] = useState<boolean>(false);

    if (newBepalingExportSearchText && newBepalingExportSearchText.endsWith(";") && newBepalingExportSearchText.length > 1) {
        setNewBepalingExportSearchText("");
        setBepalingExportSearchTexts([...(bepalingExportSearchTexts ?? []), newBepalingExportSearchText.replace(";", "")]);
    }

    const handleKeyUpEventOnnewBepalingExportSearchText = (e: any): void => {
        if ((e as KeyboardEvent).code === "Enter" && newBepalingExportSearchText && newBepalingExportSearchText.length > 0) {
            setNewBepalingExportSearchText("");
            setBepalingExportSearchTexts([...(bepalingExportSearchTexts ?? []), newBepalingExportSearchText]);
        }
    }

    const removeOption = (o: string): void => {
        setBepalingExportSearchTexts((bepalingExportSearchTexts ?? []).filter(opt => opt !== o));
    }
    
    const handleBepalingExportClick = (): void => {
        setIsExportingBepalingen(true);
        loader.show("Bepalingen exporteren...");

        const finishedCallback = () => {
            setIsExportingBepalingen(false);
            loader.hide();
        }

        let search_params: { [key: string]: any } = {}
        if (bepalingExportHoofdstuk && bepalingExportHoofdstuk.length > 0) {
            search_params['hoofdstuk_nummer'] = bepalingExportHoofdstuk;
        }
        if (bepalingExportSearchTexts && bepalingExportSearchTexts.length > 0) {
            search_params['search_text'] = bepalingExportSearchTexts.join(";");
        }

        downloadFromAPI(
            { method: "post", path: 'bepaling/export', data: { format: bepalingExportFormat, bestek_search_params: props.bestek_filter.query_params, bepaling_search_params: search_params } },
            `bepalingen.${bepalingExportFormat}`,
            {
                finally: finishedCallback
            }
        )
    }

    const fetch = useCallback(() => {
        setLoading(true);
        setBestekken([]);
        console.log(props.bestek_filter.query_params);
        apiService.post<{ result: Bestek[], total_count: number }>("/bestek/search", { 
            search_params: props.bestek_filter.query_params,
            page: viewType === "list" ? activePage : 1,
            pagesize: viewType === "list" ? 10 : 100
        }).then(result => {
            setBestekken(result.data.result);
            setTotalCount(result.data.total_count);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }, [props.bestek_filter.query_params, activePage, viewType]);

    // Oude download functie (kept for backward compatibility, maar niet meer gebruikt in UI)
    const download = (format: 'csv' | 'xlsx'): void => {
        loader.show("Export voorbereiden...");
        
        downloadFromAPI(
            { method: 'post', path: '/bestek/export', data: { search_params: props.bestek_filter.query_params, format: format } },
            `export.${format}`,
            {
                finally: loader.hide
            }
        );
    }

    // Nieuwe functie om filter params om te zetten naar BestekFilterParams formaat
    const getCurrentFilterParams = () => {
        // Converteer je Redux state terug naar BestekFilterParams formaat
        const queryParams = props.bestek_filter.query_params || {};
        
        // Converteer zoek_categorien terug van string array naar ZoekCategorie objecten
        let zoekCategorien: ZoekCategorie[] = [ZOEK_CATEGORIEEN[0]]; // Default
        if (queryParams.zoek_categorien && Array.isArray(queryParams.zoek_categorien)) {
            zoekCategorien = queryParams.zoek_categorien
                .map((value: string) => ZOEK_CATEGORIEEN.find(cat => cat.value === value))
                .filter((cat): cat is ZoekCategorie => cat !== undefined);
            
            // Als geen geldige categorieën gevonden, gebruik default
            if (zoekCategorien.length === 0) {
                zoekCategorien = [ZOEK_CATEGORIEEN[0]];
            }
        }
        
        return {
            beschrijving: Array.isArray(queryParams.beschrijving)
                ? queryParams.beschrijving.map((t: any) =>
                    typeof t === 'string' ? t : (t.exact ? `"${t.value}"` : t.value)
                  )
                : [],
            zoek_categorien: zoekCategorien,
            provincie: queryParams.provincie || null,
            gemeente: queryParams.gemeente || null,
            woonplaats: queryParams.woonplaats || null,
            jaarversie: queryParams.versie_jaarversie || null,
            release: queryParams.versie || null,
            aanmaak_datum_vanaf: queryParams.aanmaak_datum_vanaf ? new Date(queryParams.aanmaak_datum_vanaf) : undefined,
            aanmaak_datum_tm: queryParams.aanmaak_datum_tm ? new Date(queryParams.aanmaak_datum_tm) : undefined,
            wijzigings_datum_vanaf: queryParams.wijzigings_datum_vanaf ? new Date(queryParams.wijzigings_datum_vanaf) : undefined,
            wijzigings_datum_tm: queryParams.wijzigings_datum_tm ? new Date(queryParams.wijzigings_datum_tm) : undefined,
            hoofdcodes_werkcats: [], // Deze zijn al geconverteerd naar IDs in query_params
            hoofdcodes_subwerkcats: [],
            hoofdcodes: [],
            deficodes: [],
            ovks: []
        };
    }

    useEffect(() => {
        fetch();
    }, [activePage, viewType]);

    const mapHeight: number = window.screen.height - 350;

    return (
        <div className="pt-4">
            <Container fluid>
                <Row>
                    <Col>
                        <ButtonGroup aria-label="Weergave switch">
                            <Button variant={ viewType === "list" ? "primary" : "light" } onClick={(e) => setViewtype('list')}>Lijstweergave</Button>
                            <Button variant={ viewType === "map" ? "primary" : "light" }  onClick={(e) => setViewtype('map')}>Kaartweergave</Button>
                        </ButtonGroup>
                    </Col>
                    <Col>
                        <Dropdown style={{float: 'right'}}>
                            <Dropdown.Toggle variant="light">
                                Opties
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Header>Bestekken exporteren</Dropdown.Header>
                                {totalCount > 0 ? (
                                    <>
                                        <Dropdown.Item onClick={() => setCsvExportModalShown(true)}>
                                            <MdDownload className="me-1" />
                                            Aangepaste export
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.ItemText className="small text-muted">
                                            Snelle export (alle kolommen):
                                        </Dropdown.ItemText>
                                        {totalCount <= 1000 ? (
                                            <>
                                                <Dropdown.Item onClick={(e) => download('csv')}>
                                                    <MdDownload className="me-1" />CSV (snel)
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={(e) => download('xlsx')}>
                                                    <MdDownload className="me-1" />Excel (snel)
                                                </Dropdown.Item>
                                            </>
                                        ) : (
                                            <Dropdown.ItemText className="small">
                                                Snelle export alleen beschikbaar voor ≤1000 resultaten.
                                                Gebruik 'Aangepaste export' voor meer resultaten.
                                            </Dropdown.ItemText>
                                        )}
                                    </>
                                ) : (
                                    <Dropdown.ItemText>Geen bestekken om te exporteren.</Dropdown.ItemText>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Header>Bepalingen exporteren</Dropdown.Header>
                                <Dropdown.Item onClick={() => setBepalingExportModalShown(true)}>
                                    <MdDownload className="me-1" />
                                    Bepalingen uit deze bestekken
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <div style={{float: 'right', marginRight: '10px', marginTop: '8px', fontSize: '12px', fontStyle: 'italic'}}>{totalCount} {(totalCount) === 1 ? 'resultaat' : 'resultaten'}</div>
                    </Col>
                </Row>
            </Container>

            <div className="mt-4">
                <BestekFilter onFilterButtonClick={fetch} result_count={totalCount} />
            </div>

            {/* Map of bestekken */}
            {
                viewType === 'map' && (
                    <Container fluid>
                        <Row>
                            <Col className="pt-4" sm={12} md={6}>
                                { !selectedBestek && <p>Selecteer een bestek op de kaart om info te tonen.</p> }
                                { selectedBestek && <BestekGeneralInfo bestek={selectedBestek} /> }
                                { selectedBestek && (
                                        <>
                                            <br />
                                            <a href={`/bestekken/${selectedBestek.id}/${selectedBestek.registratie_nummer}`}>Toon bestek detail pagina</a>
                                        </>
                                    )
                                }
                            </Col>

                            <Col className="pt-4" sm={12} md={6}>
                                <BestekkenMap bestekken={bestekken} mapHeight={mapHeight+'px'} selectedBestek={selectedBestek} onBestekSelection={setSelectedBestek} />
                            </Col>  
                        </Row>
                    </Container>
                )
            }

            {/* List of bestekken */}
            {
                viewType === 'list' && (
                    <BestekkenList 
                        loading={loading}
                        bestekken={bestekken}
                        page={activePage}
                        onPageChange={setActivePage}
                        totalCount={totalCount}
                    />
                )
            }

            <BestekUploader show={uploadModalShown} handleClose={() => setUploadModalShown(false)} />

            {/* CSV Export Modal */}
            <CSVExportModal
                show={csvExportModalShown}
                onHide={() => setCsvExportModalShown(false)}
                filterParams={getCurrentFilterParams()}
                totalCount={totalCount}
            />

            <Modal show={bepalingExportModalShown} onHide={() => setBepalingExportModalShown(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bepalingen exporteren</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="d-flex flex-column">
                        Exporteer bepalingen uit bestekken die voldoen aan het huidige filter.<br />

                        <div style={{ width: '100%' }}>
                            <Form.FloatingLabel label="Hoofdstuknummer" className="mt-2">
                                <Form.Control type="text" value={bepalingExportHoofdstuk ?? ""} onChange={(e) => setBepalingExportHoofdstuk(e.target.value)}></Form.Control>
                            </Form.FloatingLabel>
                            <Form.Text muted>Alleen hoofdstukken met dit nummer zullen worden geëxporteerd.</Form.Text>
                        </div>

                        <div style={{ width: '100%' }}>
                            <Form.Label className="mt-4" style={{ fontSize: '16px' }}>Zoekwoorden</Form.Label>
                            <div className={styles.bepalingExportSearchTexts}>
                                { bepalingExportSearchTexts && bepalingExportSearchTexts.length > 0 && bepalingExportSearchTexts.map(o => {
                                    return (
                                        <Badge key={`option-${o}`} pill bg="primary">
                                            {o} <MdClose className={styles.option_delete_button} onClick={(e) => removeOption(o)} />
                                        </Badge>
                                    )
                                }) }
                            </div>
                            { (!bepalingExportSearchTexts || bepalingExportSearchTexts.length === 0) && <p style={{ fontSize: '10px', fontStyle: 'italic' }}>Er zijn nog geen zoekwoorden toegevoegd</p> }
                            <Form.FloatingLabel label="Nieuwe optie" className="mt-2">
                                <Form.Control type="text" value={newBepalingExportSearchText ?? ""} onChange={(e) => setNewBepalingExportSearchText(e.target.value)} onKeyUp={handleKeyUpEventOnnewBepalingExportSearchText}></Form.Control>
                            </Form.FloatingLabel>
                            <Form.Text muted>Druk op 'Enter' of ';' om het zoekwoord toe te voegen.</Form.Text>
                        </div>

                        <div style={{ width: '100%' }}>
                            <Form.Label className="mt-4" style={{ fontSize: '16px' }}>Format</Form.Label>
                            <Form>
                                <Form.Check name="format" type="radio" label="CSV" id="csv" checked={bepalingExportFormat === 'csv'} onChange={(e) => { if (e.target.checked) { setBepalingExportFormat('csv') } }} />
                                <Form.Check name="format" type="radio" label="Excel" id="excel" checked={bepalingExportFormat === 'xlsx'} onChange={(e) => { if (e.target.checked) { setBepalingExportFormat('xlsx') } }} />
                            </Form>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="primary" onClick={handleBepalingExportClick} disabled={isExportingBepalingen}>Exporteren</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default connect(mapStateToProps)(BestekkenListPage);